/**
 * AvatarUploader — V1.8.6
 *
 * Round avatar with a camera-badge overlay. Click anywhere on the tile
 * to upload; if a photo already exists you get a "Remove" button under
 * the tile. Backed by `POST /me/profile/avatar` + `DELETE`.
 *
 * Works for every role (candidate, recruiter, admin, HM, superadmin) —
 * the endpoint already accepts all of them.
 */
import React from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Camera, Trash } from "@phosphor-icons/react";
import ImageCropperSheet from "@/components/chat/ImageCropperSheet";

const ACCEPT = "image/jpeg,image/png,image/webp";
const MAX_BYTES = 5 * 1024 * 1024;  // V1.9.34 — raw file limit (cropper compresses)

export default function AvatarUploader({
    initialUrl,
    initials = "?",
    size = "md",           // "sm" | "md" | "lg"
    onChange,              // (nextUrl | null) => void
    testIdPrefix = "avatar-uploader",
    variant = "cobalt",    // "cobalt" | "green"
}) {
    const [preview, setPreview] = React.useState(initialUrl || null);
    const [busy, setBusy] = React.useState(false);
    const [pendingFile, setPendingFile] = React.useState(null);  // V1.9.34 — cropper input
    const inputRef = React.useRef(null);

    React.useEffect(() => { setPreview(initialUrl || null); }, [initialUrl]);

    const sizeCls = size === "lg"
        ? "w-32 h-32 text-4xl"
        : size === "sm"
            ? "w-14 h-14 text-lg"
            : "w-24 h-24 text-3xl";

    const bg = variant === "green"
        ? "bg-gradient-to-br from-emerald-500 to-emerald-600"
        : "bg-gradient-to-br from-cobalt to-cobalt/80";

    const onFile = (file) => {
        if (!file) return;
        if (!["image/jpeg", "image/jpg", "image/png", "image/webp"].includes(file.type)) {
            toast.error("JPEG, PNG or WEBP only.");
            return;
        }
        if (file.size > MAX_BYTES) {
            toast.error("Max 5 MB. Please compress and try again.");
            return;
        }
        // V1.9.34 — open cropper instead of uploading raw.
        setPendingFile(file);
    };

    const onCropped = async (blob) => {
        setPendingFile(null);
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", blob, "avatar.png");
            const r = await api.post("/me/profile/avatar", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            const next = r.data?.avatar_url || null;
            setPreview(next);
            onChange?.(next);
            toast.success("Photo updated.");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Upload failed.");
        } finally {
            setBusy(false);
            if (inputRef.current) inputRef.current.value = "";
        }
    };

    const remove = async () => {
        setBusy(true);
        try {
            await api.delete("/me/profile/avatar");
            setPreview(null);
            onChange?.(null);
            toast.success("Photo removed.");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't remove photo.");
        } finally { setBusy(false); }
    };

    return (
        <div className="flex flex-col items-center gap-3">
            <label
                className={`relative cursor-pointer group rounded-full ${sizeCls} ${bg}
                    text-white font-black flex items-center justify-center overflow-hidden shadow-lg ring-4 ring-white
                    ${busy ? "opacity-70" : ""}`}
                data-testid={`${testIdPrefix}-tile`}
            >
                <input
                    ref={inputRef}
                    type="file"
                    accept={ACCEPT}
                    className="hidden"
                    onChange={(e) => onFile(e.target.files?.[0])}
                    disabled={busy}
                    data-testid={`${testIdPrefix}-input`}
                />
                {preview ? (
                    <img
                        src={preview}
                        alt="Your profile photo"
                        className="w-full h-full object-cover"
                        data-testid={`${testIdPrefix}-preview`}
                    />
                ) : (
                    <span data-testid={`${testIdPrefix}-initial`}>{initials}</span>
                )}
                <span
                    className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity"
                    aria-hidden
                >
                    <Camera size={size === "lg" ? 28 : 20} weight="fill" className="text-white" />
                </span>
                {/* Persistent camera badge (mobile has no hover). */}
                <span className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-white ring-2 ring-white shadow flex items-center justify-center">
                    <Camera size={14} weight="fill" className="text-cobalt" />
                </span>
            </label>
            <div className="flex items-center gap-2">
                <button
                    type="button"
                    onClick={() => inputRef.current?.click()}
                    disabled={busy}
                    data-testid={`${testIdPrefix}-btn`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-black uppercase tracking-wider bg-ink text-white hover:opacity-90 disabled:opacity-50"
                >
                    <Camera size={12} weight="bold" />
                    {busy ? "Uploading…" : preview ? "Change photo" : "Add photo"}
                </button>
                {preview && (
                    <button
                        type="button"
                        onClick={remove}
                        disabled={busy}
                        data-testid={`${testIdPrefix}-remove`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-black uppercase tracking-wider border border-ink/15 text-red-600 hover:border-red-500 disabled:opacity-50"
                    >
                        <Trash size={12} weight="bold" /> Remove
                    </button>
                )}
            </div>
            <p className="text-[10px] text-ink/50">JPEG · PNG · WEBP · Max 5 MB · Crop before saving</p>
            <ImageCropperSheet
                file={pendingFile}
                onCancel={() => setPendingFile(null)}
                onCropped={onCropped}
                title="Adjust profile photo"
            />
        </div>
    );
}
